/**
 * Every human-facing date and duration string in the app.
 *
 * These were four helpers scattered across four files, one of them (`when`) a
 * byte-identical copy in pages/Feed.jsx and components/ArticlePeekDialog.jsx.
 * `now` is a parameter on the relative ones so a test can state the moment it
 * is asking about instead of racing the clock.
 */

const LONG_DATE = { year: "numeric", month: "long", day: "numeric" };

/** "2026-08-21" — a calendar day with no time and no zone attached to it. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A publish date, rendered as the day it says it is.
 *
 * This used to be `new Date(Date.parse(d))`, and for a date-only string that is
 * the wrong day for most of the world: the spec parses "2026-08-21" as UTC
 * midnight, and toLocaleDateString then renders it in the reader's zone — so
 * everyone west of Greenwich saw "August 20, 2026". Date-only publish_date is
 * the normal case, not an edge one. A bare calendar day has no zone, so it is
 * built as a local date and never shifted. Anything carrying a time (an ISO
 * timestamp with an offset) is a real instant and is still converted.
 */
export function fmtDate(value) {
  if (value == null || value === "") return value;
  const s = String(value).trim();

  const day = DATE_ONLY.exec(s);
  if (day) {
    const d = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, LONG_DATE);
  }

  const t = Date.parse(s);
  return Number.isNaN(t) ? value : new Date(t).toLocaleDateString(undefined, LONG_DATE);
}

/**
 * How long ago an article was published, for a ticker card.
 *
 * Two things this did not used to get right. A `published_at` in the future
 * (a feed that stamps ahead, or a reader whose clock is behind) produced a
 * negative minute count, which `Math.max(1, mins)` clamped to "1m ago" — a
 * confident lie. And the hours branch rounded, so 1439 minutes — twenty-three
 * hours and fifty-nine minutes, still inside the same branch — rendered as
 * "24h ago", a duration this function is supposed to have handed to the date
 * branch. Floor is what "h ago" means: 23h ago until it is a day.
 */
export function when(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.round((now - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "Today", "Yesterday", then the weekday, then the date — as you'd say it. */
export function dayLabel(iso, now = Date.now()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Earlier";
  const today = new Date(now);
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(d)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

/**
 * Rows collapsed into consecutive runs sharing a day label.
 *
 * Runs, deliberately, not a keyed bucket: the list arrives newest-first and
 * stays in that order, so a label only ever begins a new section when the day
 * actually changes. Grouping by key would silently reorder an unsorted list
 * into something that looks sorted and isn't.
 */
export function groupByDay(rows, getDate = (r) => r.created_at, now = Date.now()) {
  const out = [];
  (rows ?? []).forEach((r) => {
    const label = dayLabel(getDate(r), now);
    const last = out[out.length - 1];
    if (last && last.label === label) last.rows.push(r);
    else out.push({ label, rows: [r] });
  });
  return out;
}

/** When the daily scan allowance comes back, in words. */
export function resetsIn(iso, now = Date.now()) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const mins = Math.round((t - now) / 60000);
  if (mins <= 0) return "any moment now";
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? "in about an hour" : `in about ${hours} hours`;
}
